import { Reservation } from '@luxuryescapes/contract-svc-reservation';

export type PromotionRoomScope = Partial<Pick<Reservation.Discount, 'is_room_type_scoped' | 'room_type_ids'>>;

export type PromotionRoomScopeDisplay =
  | { kind: 'rate-plan' }
  | { kind: 'no-rooms' }
  | { kind: 'room-names'; roomNames: Array<string> }
  | { kind: 'room-count'; roomCount: number };

/**
 * Describes which rooms a promotion covers, for display in the admin portal.
 *
 * `is_room_type_scoped` is the authority rather than the array length: svc-reservation
 * deletes a scoped promotion outright once it has no rooms left, so an empty
 * list here is only ever a stale read, not a state that persists.
 *
 * Returns null when the promotion covers the whole rate plan, so callers can
 * omit the label entirely.
 */
export function formatPromotionRoomScope(promotion: PromotionRoomScope): string | null {
  if (!promotion.is_room_type_scoped) {
    return null;
  }

  const scopedRoomCount = promotion.room_type_ids?.length ?? 0;

  if (scopedRoomCount === 0) {
    return 'No rooms - not applied';
  }

  return `${scopedRoomCount} room${scopedRoomCount === 1 ? '' : 's'} only`;
}

/** True when a promotion is scoped to room types but lists none. */
export function isPromotionAppliedToNoRooms(promotion: PromotionRoomScope): boolean {
  return Boolean(promotion.is_room_type_scoped) && !promotion.room_type_ids?.length;
}

// Falls back to a bare count when any scoped id can't be named from the rooms we have.
export function getPromotionRoomScopeDisplay(
  promotion: PromotionRoomScope,
  ratePlanRooms: Array<{ id: string; name: string }>,
): PromotionRoomScopeDisplay {
  if (!promotion.is_room_type_scoped) {
    return { kind: 'rate-plan' };
  }

  const roomTypeIds = promotion.room_type_ids ?? [];

  if (!roomTypeIds.length) {
    return { kind: 'no-rooms' };
  }

  const roomNames = ratePlanRooms.filter((room) => roomTypeIds.includes(room.id)).map((room) => room.name);

  if (roomNames.length < roomTypeIds.length) {
    return { kind: 'room-count', roomCount: roomTypeIds.length };
  }

  return { kind: 'room-names', roomNames };
}
